import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  scheduleOpt,
  videoAnalyzer,
  checkStatus,
  delayAnalysis,
  overallReport,
  riskForecast,
  AI_BASE_URL
} from '../../services/aiBackend';
import { aiJobsService, AIJobType } from '../../services/aiJobsService';

interface DashboardStats {
  totalPlans: number;
  activePlans: number;
  completedPlans: number;
  overduePlans: number;
  totalBudget: number;
  actualSpent: number;
  avgProgress: number;
}

const ProjectPlanningDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [projectPlans] = useState([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // AI Backend endpoint states
  const [xerFile, setXerFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiResult, setAiResult] = useState<any>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [statusParams, setStatusParams] = useState<{ task_id: string; data_key: string }>({ task_id: '', data_key: '' });
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>('');

  useEffect(() => {
    checkAuthentication();
  }, []);

  const onXerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setXerFile(file);
    if (file) {
      await handleScheduleOpt();
    }
  };

  const onVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setVideoFile(file);
    if (file && xerFile) {
      await handleVideoAnalyzer();
    }
  };

  const onImagesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setImages(files);
    if (files.length > 0 && xerFile) {
      await handleOverallReport();
    }
  };

  const [aiHttpStatus, setAiHttpStatus] = useState<number | null>(null);
  const [aiTaskId, setAiTaskId] = useState<string | null>(null);
  const [aiDataKey, setAiDataKey] = useState<string | null>(null);

  const runCall = async (fn: () => Promise<any>, type?: AIJobType) => {
    try {
      setAiError(null);
      setAiResult(null);
      setAiTaskId(null);
      setAiDataKey(null);
      setAiHttpStatus(null);
      setAiLoading(true);
      const res = await fn();
      setAiResult(res?.data ?? res);
      setAiHttpStatus(res?.status ?? null);
      const taskId = res?.data?.task_id || res?.data?.out_params?.task_id || null;
      const dataKey = res?.data?.data_key || res?.data?.out_params?.data_key || null;
      setAiTaskId(taskId);
      setAiDataKey(dataKey);
      // Persist job for later status checks
      if (type && taskId && dataKey) {
        try {
          await aiJobsService.create({
            type,
            task_id: taskId,
            data_key: dataKey,
            meta: {
              fileName: xerFile?.name,
              videoFileName: videoFile?.name,
              imageCount: images.length,
            },
          });
          await refreshJobs();
        } catch (e) {
          // Swallow errors to not disrupt UX
          console.warn('Failed to save AI job', e);
        }
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Request failed';
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const extractAnalysisText = (res: any): string | undefined => {
    if (!res) return undefined;
    if (typeof res === 'string') return res;
    if (typeof res?.data === 'string') return res.data;
    if (typeof res?.result === 'string') return res.result;
    if (typeof res?.data?.result === 'string') return res.data.result;
    if (typeof res?.summary === 'string') return res.summary;
    return undefined;
  };

  const handleViewDetails = () => {
    const analysisText = extractAnalysisText(aiResult);
    navigate('/project-planning/ai-details', {
      state: {
        analysisText,
        taskId: aiTaskId,
        dataKey: aiDataKey,
      },
    });
  };

  const handleScheduleOpt = async () => {
    if (!xerFile) { setAiError('Please select a .xer file'); return; }
    await runCall(() => scheduleOpt(xerFile), 'schedule_opt');
  };

  const handleVideoAnalyzer = async () => {
    if (!xerFile || !videoFile) { setAiError('Select both .xer and video files'); return; }
    await runCall(() => videoAnalyzer(xerFile, videoFile), 'video_analyzer');
  };

  const handleDelay = async () => {
    if (!xerFile) { setAiError('Please select a .xer file'); return; }
    await runCall(() => delayAnalysis(xerFile), 'delay');
  };

  const handleOverallReport = async () => {
    if (!xerFile) { setAiError('Please select a .xer file'); return; }
    await runCall(() => overallReport(xerFile, images), 'overall_report');
  };

  const handleRiskForecast = async () => {
    if (!xerFile) { setAiError('Please select a .xer file'); return; }
    await runCall(() => riskForecast(xerFile), 'risk_forecast');
  };

  const handleStatus = async () => {
    if (!statusParams.task_id || !statusParams.data_key) { setAiError('Enter task_id and data_key'); return; }
    await runCall(() => checkStatus(statusParams.task_id, statusParams.data_key));
  };

  const refreshJobs = async () => {
    try {
      const res = await aiJobsService.list();
      setSavedJobs(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      setSavedJobs([]);
    }
  };

  const copyToClipboard = async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  const checkAuthentication = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Auto-login with test credentials for development
      await loginWithTestCredentials();
    } else {
      setIsAuthenticated(true);
      fetchData();
      // Ensure saved jobs are fetched on page load when already authenticated
      await refreshJobs();
    }
  };

  const loginWithTestCredentials = async () => {
    try {
      const response = await api.post('/auth/login', {
        email: 'admin@construction.com',
        password: 'admin123'
      });
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        setIsAuthenticated(true);
        fetchData();
        refreshJobs();
      }
    } catch (err: any) {
      setError('Failed to authenticate. Please check if the backend is running.');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const statsResponse = await api.get('/project-plans/dashboard');
      setStats(statsResponse.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'primary';
      case 'on-hold': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'success';
    if (progress >= 50) return 'warning';
    return 'error';
  };

  // Removed authentication gating to allow direct access to AI endpoints UI

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Project Planning Dashboard
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Plans
                    </Typography>
                    <Typography variant="h4">
                      {stats?.totalPlans ?? 0}
                    </Typography>
                  </Box>
                  <TimelineIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Active Plans
                    </Typography>
                    <Typography variant="h4">
                      {stats?.activePlans ?? 0}
                    </Typography>
                  </Box>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Overdue Plans
                    </Typography>
                    <Typography variant="h4">
                      {stats?.overduePlans ?? 0}
                    </Typography>
                  </Box>
                  <WarningIcon color="error" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Avg Progress
                    </Typography>
                    <Typography variant="h4">
                      {(stats?.avgProgress ?? 0).toFixed(1)}%
                    </Typography>
                  </Box>
                  <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* AI Backend Endpoints (Railway) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(60% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AI Backend Endpoints
              </Typography>


              {aiError && (
                <Alert severity="error" sx={{ mb: 2 }}>{aiError}</Alert>
              )}
              {aiLoading && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                </Box>
              )}

              {/* File inputs */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Button variant="outlined" component="label">
                  Select .xer file
                  <input hidden type="file" accept=".xer" onChange={onXerChange} />
                </Button>
                <Button variant="outlined" component="label">
                  Select video
                  <input hidden type="file" accept="video/*" onChange={onVideoChange} />
                </Button>
                <Button variant="outlined" component="label">
                  Select images
                  <input hidden type="file" accept="image/*" multiple onChange={onImagesChange} />
                </Button>
                {xerFile ? (<Chip label={xerFile.name} size="small" />) : null}
                {videoFile ? (<Chip label={videoFile.name} size="small" />) : null}
                {images.length > 0 && (<Chip label={`${images.length} images`} size="small" />)}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Requirements: .xer required for most; video for Video Analyzer; images optional for Overall Report.
              </Typography>

              {/* Endpoint actions */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button variant="contained" disabled={!xerFile || aiLoading} onClick={handleScheduleOpt} startIcon={<ScheduleIcon />}>Schedule Optimize</Button>
                <Button variant="contained" disabled={!xerFile || !videoFile || aiLoading} onClick={handleVideoAnalyzer} startIcon={<AssessmentIcon />}>Video Analyzer</Button>
                <Button variant="contained" disabled={!xerFile || aiLoading} onClick={handleDelay} startIcon={<FlagIcon />}>Delay Analysis</Button>
                <Button variant="contained" disabled={!xerFile || aiLoading} onClick={handleOverallReport} startIcon={<TrendingUpIcon />}>Overall Report</Button>
                <Button variant="contained" disabled={!xerFile || aiLoading} onClick={handleRiskForecast} startIcon={<WarningIcon />}>Risk Forecast</Button>
              </Box>

              {/* Status Checker */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Check Status</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel id="job-select-label">Saved Jobs</InputLabel>
                    <Select
                      labelId="job-select-label"
                      label="Saved Jobs"
                      value={selectedJobId}
                      onChange={(e) => {
                        const id = String(e.target.value);
                        setSelectedJobId(id);
                        const job = savedJobs.find((j) => String(j._id) === id);
                        if (job) {
                          setStatusParams({ task_id: job.task_id, data_key: job.data_key });
                        }
                      }}
                    >
                      {savedJobs.map((job) => (
                        <MenuItem key={job._id} value={job._id}>
                          {`${job.type} • ${job.task_id?.slice(0, 8)}…`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={refreshJobs}>Refresh List</Button>
                  <Button variant="contained" disabled={aiLoading || !selectedJobId} onClick={handleStatus}>Query Status</Button>
                </Box>
              </Box>

              {/* Response */}
              {(aiResult || aiHttpStatus) && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity={aiHttpStatus === 202 ? 'info' : 'success'} sx={{ mb: 2 }}>
                    {aiHttpStatus ? `HTTP ${aiHttpStatus}` : 'Response received'}
                    {aiHttpStatus === 202 ? ' • Accepted: use Status to poll result' : ''}
                  </Alert>
                  {(aiTaskId || aiDataKey) && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {aiTaskId && (<Chip label={`task_id: ${aiTaskId}`} onClick={() => copyToClipboard(aiTaskId!)} />)}
                      {aiDataKey && (<Chip label={`data_key: ${aiDataKey}`} onClick={() => copyToClipboard(aiDataKey!)} />)}
                    </Box>
                  )}
                  {aiResult && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(aiResult, null, 2)}
                    </Typography>
                  )}
                  {aiHttpStatus === 200 && (
                    <Box sx={{ mt: 2 }}>
                      <Button variant="contained" onClick={handleViewDetails}>View Details</Button>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(40% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tips
              </Typography>
              <Typography variant="body2" color="text.secondary">
                - 202 Accepted: background processing. Use task_id and data_key with Status.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                - 200 OK: cached or quick result returned immediately.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                - Ensure files meet type and size constraints.
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Saved AI Jobs</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell>task_id</TableCell>
                      <TableCell>data_key</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedJobs.map((job) => (
                      <TableRow key={job._id} hover>
                        <TableCell>{job.type}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {job?.meta?.fileName || job?.meta?.videoFileName || '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.task_id}</TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.data_key}</TableCell>
                        <TableCell>{new Date(job.createdAt).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Project Plans List removed to align with AI endpoints-only design */}

      {/* Floating Action Buttons removed */}

      {/* Context Menu removed */}

      {/* Create Plan Dialog removed */}
    </Box>
  );
};

export default ProjectPlanningDashboard;